"use client"

import { useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react"
import { OfferRealtimeContext } from "@/components/providers/offer-realtime-provider"
import { generateClientOfferId } from "@/lib/offers/offer-utils"
import {
  acceptOffer as acceptOfferBuilder,
  counterOffer as counterOfferBuilder,
  declineOffer as declineOfferBuilder,
  placeOffer as placeOfferBuilder,
  withdrawOffer as withdrawOfferBuilder,
  type CounterOfferIntent,
  type OfferBuilderResult,
  type PlaceOfferIntent,
} from "@/lib/offers/offer-builders"
import type {
  OfferSlot,
  ListingOffersAggregate,
  OfferWithRelations,
  PlaceOfferRpcResult,
  AcceptOfferRpcResult,
  DeclineOfferRpcResult,
  CounterOfferRpcResult,
  WithdrawOfferRpcResult,
} from "@/lib/offers/offer-types"

function useOfferContext() {
  const ctx = useContext(OfferRealtimeContext)
  if (!ctx) throw new Error("useOffer hooks must be used within OfferRealtimeProvider")
  return ctx
}

interface UseOfferOptions {
  initial?: Partial<OfferSlot>
}

/** Subscribe to a single offer's realtime state. */
export function useOffer(offerId: string | null, options?: UseOfferOptions): OfferSlot | null {
  const ctx = useOfferContext()

  useEffect(() => {
    if (!offerId) return
    ctx.subscribeOffer(offerId, options?.initial)
    return () => ctx.unsubscribeOffer(offerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId])

  const subscribe = useMemo(
    () => (cb: () => void) => {
      if (!offerId) return () => {}
      return ctx.addOfferListener(offerId, cb)
    },
    [offerId, ctx]
  )
  const getSnapshot = useMemo(
    () => () => (offerId ? ctx.getOfferSnapshot(offerId) : null),
    [offerId, ctx]
  )

  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

interface UseListingOffersOptions {
  initial?: Partial<ListingOffersAggregate>
}

/** Subscribe to a listing's offer aggregates (counts, highest pending). */
export function useListingOffers(
  listingId: string | null,
  options?: UseListingOffersOptions
): ListingOffersAggregate | null {
  const ctx = useOfferContext()

  useEffect(() => {
    if (!listingId) return
    ctx.subscribeListing(listingId, options?.initial)
    return () => ctx.unsubscribeListing(listingId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId])

  const subscribe = useMemo(
    () => (cb: () => void) => {
      if (!listingId) return () => {}
      return ctx.addListingListener(listingId, cb)
    },
    [listingId, ctx]
  )
  const getSnapshot = useMemo(
    () => () => (listingId ? ctx.getListingSnapshot(listingId) : null),
    [listingId, ctx]
  )

  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

/**
 * Stable action callbacks for offer mutations. Doesn't subscribe to any state.
 *
 * Behavior layer only: every call delegates body assembly + boundary safeParse
 * + fetch to `lib/offers/offer-builders.ts` (G16/G18 extract, 2026-06-10).
 * The hook composes the builder call with `ctx.refetchOffer(...)` so the
 * realtime cache invalidates on success — that's the only thing this layer
 * adds beyond the lib builders.
 *
 * @see lib/offers/offer-builders.ts — client-side builders + boundary safeParse
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §3.2 G16
 */
export function useOfferActions() {
  const ctx = useOfferContext()

  return useMemo(
    () => ({
      generateClientId: generateClientOfferId,
      placeOffer: (input: PlaceOfferIntent): Promise<OfferBuilderResult> =>
        placeOfferBuilder(input),
      acceptOffer: async (offerId: string): Promise<OfferBuilderResult> => {
        const r = await acceptOfferBuilder(offerId)
        if (r.ok) void ctx.refetchOffer(offerId)
        return r
      },
      declineOffer: async (
        offerId: string,
        reason?: string,
      ): Promise<OfferBuilderResult> => {
        const r = await declineOfferBuilder(offerId, reason)
        if (r.ok) void ctx.refetchOffer(offerId)
        return r
      },
      counterOffer: async (
        input: CounterOfferIntent,
      ): Promise<OfferBuilderResult> => {
        const r = await counterOfferBuilder(input)
        if (r.ok) void ctx.refetchOffer(input.parentOfferId)
        return r
      },
      withdrawOffer: async (offerId: string): Promise<OfferBuilderResult> => {
        const r = await withdrawOfferBuilder(offerId)
        if (r.ok) void ctx.refetchOffer(offerId)
        return r
      },
      refetchOffer: ctx.refetchOffer,
      refetchListing: ctx.refetchListing,
    }),
    [ctx]
  )
}

interface UseMyOffersOptions {
  type?: "sent" | "received"
  status?: string
  enabled?: boolean
}

interface MyOffersState {
  offers: OfferWithRelations[]
  loading: boolean
  error: string | null
  nextCursor: string | null
  hasMore: boolean
}

/** Fetch + paginate the current user's offers. Refetched on visibility / auth changes. */
export function useMyOffers(options: UseMyOffersOptions = {}): MyOffersState & {
  refetch: () => Promise<void>
  loadMore: () => Promise<void>
} {
  const { type, status, enabled = true } = options
  const [state, setState] = useState<MyOffersState>({
    offers: [],
    loading: true,
    error: null,
    nextCursor: null,
    hasMore: false,
  })

  const buildQuery = useCallback(
    (cursor?: string) => {
      const params = new URLSearchParams()
      if (type) params.set("type", type)
      if (status) params.set("status", status)
      if (cursor) params.set("cursor", cursor)
      params.set("limit", "20")
      return params.toString()
    },
    [type, status]
  )

  const refetch = useCallback(async () => {
    if (!enabled) return
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const res = await fetch(`/api/offers?${buildQuery()}`, { credentials: "include" })
      if (!res.ok) {
        setState((s) => ({ ...s, loading: false, error: `HTTP ${res.status}` }))
        return
      }
      const json = await res.json()
      setState({
        offers: Array.isArray(json.offers) ? json.offers : [],
        loading: false,
        error: null,
        nextCursor: typeof json.nextCursor === "string" ? json.nextCursor : null,
        hasMore: Boolean(json.nextCursor),
      })
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err instanceof Error ? err.message : "Network error" }))
    }
  }, [buildQuery, enabled])

  const loadMore = useCallback(async () => {
    if (!enabled) return
    setState((s) => {
      if (!s.nextCursor || s.loading) return s
      return { ...s, loading: true }
    })
    try {
      const res = await fetch(`/api/offers?${buildQuery(state.nextCursor ?? undefined)}`, {
        credentials: "include",
      })
      if (!res.ok) {
        setState((s) => ({ ...s, loading: false, error: `HTTP ${res.status}` }))
        return
      }
      const json = await res.json()
      setState((s) => ({
        offers: [...s.offers, ...(Array.isArray(json.offers) ? json.offers : [])],
        loading: false,
        error: null,
        nextCursor: typeof json.nextCursor === "string" ? json.nextCursor : null,
        hasMore: Boolean(json.nextCursor),
      }))
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err instanceof Error ? err.message : "Network error" }))
    }
  }, [buildQuery, enabled, state.nextCursor])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const ctx = useOfferContext()
  useEffect(() => {
    return ctx.addOfferListListener(() => {
      void refetch()
    })
  }, [ctx, refetch])

  return { ...state, refetch, loadMore }
}

/** Count of offers needing attention (respond, counter, or pay after accept). */
export function useOfferActionCount(): number {
  const ctx = useOfferContext()
  const subscribe = useMemo(() => (cb: () => void) => ctx.addActionCountListener(cb), [ctx])
  const getSnapshot = useMemo(() => () => ctx.actionCount(), [ctx])
  return useSyncExternalStore(subscribe, getSnapshot, () => 0)
}

/** Notification badge count, kept fresh via the realtime provider. */
export function useOfferUnreadCount(): number {
  const ctx = useOfferContext()
  const subscribe = useMemo(() => (cb: () => void) => ctx.addNotificationListener(cb), [ctx])
  const getSnapshot = useMemo(() => () => ctx.unreadNotificationCount(), [ctx])
  return useSyncExternalStore(subscribe, getSnapshot, () => 0)
}

export function useOfferNotificationActions() {
  const ctx = useOfferContext()
  return useMemo(
    () => ({
      markRead: ctx.markNotificationsRead,
      markAllRead: ctx.markAllNotificationsRead,
    }),
    [ctx]
  )
}

// Re-export RPC result types for convenience.
export type {
  PlaceOfferRpcResult,
  AcceptOfferRpcResult,
  DeclineOfferRpcResult,
  CounterOfferRpcResult,
  WithdrawOfferRpcResult,
}
